
import React from 'react';
import { IntegrationApp, Language } from '../../types';
import { TRANSLATIONS } from '../../constants';

interface IntegrationsDashboardProps {
  language: Language;
  selectedApp: IntegrationApp;
  isConnecting: boolean;
  handleConnect: (appId: string) => void;
  // Specific props
  destinationInput: string;
  setDestinationInput: (val: string) => void;
  startNavigation: () => void;
  setActiveTab: (tab: 'dashboard' | 'settings' | 'live') => void;
  setCallStatus: (status: 'incoming' | 'active' | 'ended') => void;
  setIsSimulating: (val: boolean) => void;
}

const IntegrationsDashboard: React.FC<IntegrationsDashboardProps> = ({ 
  language, selectedApp, isConnecting, handleConnect,
  destinationInput, setDestinationInput, startNavigation,
  setActiveTab, setCallStatus, setIsSimulating
}) => {
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';

  return (
     <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-bottom-4">
        <div className="bg-white dark:bg-[#1a1a20] p-6 rounded-3xl border border-slate-200 dark:border-white/5">
           <h3 className="text-sm font-black text-slate-500 uppercase tracking-widest mb-4">Performance</h3>
           <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-slate-50 dark:bg-black/20 rounded-2xl">
                 <p className="text-xs text-slate-400 font-bold mb-1">Total Requests</p>
                 <p className="text-2xl font-black text-slate-800 dark:text-white">{selectedApp.stats?.requests || 0}</p>
              </div>
              <div className="p-4 bg-slate-50 dark:bg-black/20 rounded-2xl">
                 <p className="text-xs text-slate-400 font-bold mb-1">Time Saved</p>
                 <p className="text-2xl font-black text-emerald-500">{Math.floor(selectedApp.stats?.timeSaved || 0)}m</p>
              </div>
           </div>
           
           <button 
              onClick={() => handleConnect(selectedApp.id)}
              disabled={isConnecting}
              className={`w-full py-3 rounded-xl font-black text-xs transition-all ${selectedApp.connected ? 'bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white' : 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'}`}
           >
              {isConnecting ? <i className="fas fa-spinner fa-spin"></i> : (selectedApp.connected ? t.disconnect : t.connect)}
           </button>
        </div>

        <div className="bg-white dark:bg-[#1a1a20] p-6 rounded-3xl border border-slate-200 dark:border-white/5 flex flex-col justify-center items-center text-center">
           {selectedApp.id === 'google-maps' ? (
              <div className="w-full">
                 <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 text-2xl mb-4 mx-auto"><i className="fas fa-location-dot"></i></div>
                 <h4 className="text-lg font-black dark:text-white">Smart Navigation</h4>
                 <div className="relative mb-4 mt-6">
                    <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                    <input 
                       type="text" 
                       value={destinationInput}
                       onChange={(e) => setDestinationInput(e.target.value)}
                       placeholder={isRtl ? "إلى أين تريد الذهاب؟" : "Where to?"}
                       className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm font-bold dark:text-white outline-none focus:border-blue-500 transition-colors"
                    />
                 </div>
                 <button onClick={startNavigation} disabled={!destinationInput} className="w-full px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-black text-xs shadow-lg shadow-blue-500/20 transition-all">Start Navigation</button>
              </div>
           ) : selectedApp.id === 'phone-translator' ? (
              <>
                 <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-500 text-2xl mb-4"><i className="fas fa-headset"></i></div>
                 <h4 className="text-lg font-black dark:text-white">Live Call Translator</h4>
                 <button onClick={() => { setActiveTab('live'); setCallStatus('incoming'); setIsSimulating(true); }} className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-black text-xs shadow-lg shadow-purple-500/20 transition-all mt-6">Simulate Incoming Call</button>
              </>
           ) : (
              <p className="text-sm font-bold text-slate-500">Configuration available in Settings</p>
           )}
        </div>
     </div>
  );
};

export default IntegrationsDashboard;
